import { Box, Typography } from '@mui/material'
import { memo } from 'react'
import type { TEnumItem, TSong, TDraftReviewSong } from '@modules/types'
import { getEnumNameById } from '@modules/api-core'
import {
  getCurrencySymbol,
  getTextColor,
} from '@screens/requests/components/PublisherRequest/common/feeDisplayUtils'
import { getCounterOfferInfo, getDisplayPrice } from '../utils'
import { PriceDisplay } from './PriceDisplay'
import { PriceInput } from './PriceInput'
import { ActionButtons } from './ActionButtons'

interface SingleFeeQuotedPriceProps {
  song: TSong
  songHasStatus: boolean
  isLoading: boolean
  initialLoading: boolean
  editingState: { [key: string]: boolean }
  feeQuotedPriceValues: { [key: string]: string }
  draftReviewSong: TDraftReviewSong
  distributionMethod: number
  feeType: 'print' | 'digital'
  label: string
  enums: TEnumItem[]
  onEdit: (songId: number, feeType?: 'print' | 'digital') => void
  onChange: (songId: number, value: string, feeType?: 'print' | 'digital') => void
  onSave: (songId: number, feeType: 'print' | 'digital') => void
  onCancel: (songId: number, feeType?: 'print' | 'digital') => void
  isBlocked?: boolean
  processingSongId?: number | null
  validationError?: string
  approvalDecision?: {
    isApproved: number
  } | null
}

export const SingleFeeQuotedPrice = memo(
  ({
    song,
    songHasStatus,
    isLoading,
    initialLoading,
    editingState,
    feeQuotedPriceValues,
    draftReviewSong,
    distributionMethod,
    feeType,
    label,
    enums,
    onEdit,
    onChange,
    onSave,
    onCancel,
    isBlocked,
    processingSongId,
    validationError,
    approvalDecision,
  }: SingleFeeQuotedPriceProps) => {
    const key = `${song.id}_${feeType}`
    const isEditing = editingState[key] || false
    const textColor = getTextColor(songHasStatus, isLoading)
    const feeQuotedPriceValue = feeQuotedPriceValues[key] || ''
    const displayPrice = getDisplayPrice(
      feeQuotedPriceValue,
      draftReviewSong,
      song,
      distributionMethod,
      feeType,
    )

    const currencyId =
      feeType === 'print' ? song.printPublisherCurrency : song.digitalPublisherCurrency
    const currencyName = getEnumNameById(enums, 'request_currency', currencyId || 1)
    const currency = getCurrencySymbol(currencyName)
    const counterOfferInfo = getCounterOfferInfo(song, feeType)

    return (
      <Box display="flex" px={3}>
        <Box
          display="flex"
          alignItems="flex-end"
          justifyContent="space-between"
          gap={3.5}
          sx={{ height: 50, color: textColor }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="caption" color={textColor}>
              {label}
            </Typography>

            {isEditing ? (
              <PriceInput
                value={feeQuotedPriceValue}
                onChange={(songId, value) => onChange(songId, value, feeType)}
                songId={song.id}
                initialLoading={initialLoading}
                currency={currency}
                isBlocked={isBlocked}
                validationError={validationError}
              />
            ) : (
              <PriceDisplay
                price={displayPrice}
                color={textColor}
                currency={currency}
                initialLoading={initialLoading}
                counterOfferInfo={counterOfferInfo}
                enums={enums}
                validationError={validationError}
              />
            )}
          </Box>

          <ActionButtons
            isEditing={isEditing}
            isLoading={isLoading}
            initialLoading={initialLoading}
            songId={song.id}
            onSave={onSave}
            onCancel={onCancel}
            onEdit={onEdit}
            songHasStatus={songHasStatus}
            feeType={feeType}
            isBlocked={isBlocked}
            processingSongId={processingSongId}
            approvalDecision={approvalDecision}
          />
        </Box>
      </Box>
    )
  },
)
